import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, CircularProgress, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface ReportScanDialogProps {
    open: boolean;
    onClose: () => void;
    cargoResponse: any;
    handleReportScan: (cargoResponse: any, dims: { Length: number; Width: number; Height: number }) => Promise<void>;
}

export default function ReportScanDialog({ open, onClose, cargoResponse, handleReportScan }: ReportScanDialogProps) {
    const { t } = useTranslation();
    const [length, setLength] = useState<string>('');
    const [width, setWidth] = useState<string>('');
    const [height, setHeight] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [errorText, setErrorText] = useState('');

    useEffect(() => {
        if (open) {
            setLength('');
            setWidth('');
            setHeight('');
            setLoading(false);
            setSubmitted(false);
            setErrorText('');
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!cargoResponse || !handleReportScan) return;

        const parsedLength = Number(length);
        const parsedWidth = Number(width);
        const parsedHeight = Number(height);

        if (
            !Number.isFinite(parsedLength) || parsedLength < 0 ||
            !Number.isFinite(parsedWidth) || parsedWidth < 0 ||
            !Number.isFinite(parsedHeight) || parsedHeight < 0
        ) {
            setErrorText('Please enter valid (non-negative) dimensions');
            return;
        }

        setLoading(true);
        setErrorText('');
        try {
            await handleReportScan(cargoResponse, {
                Length: parsedLength,
                Width: parsedWidth,
                Height: parsedHeight,
            });
            setSubmitted(true);
        } catch (err) {
            setErrorText(
                err instanceof Error
                    ? err.message
                    : (t('control.centerControl.auditDims.errorSubmitting') || 'Error submitting, please close and try again')
            );
        } finally {
            setLoading(false);
        }
    };

    const unitLabel = cargoResponse?.units?.length ?? '';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>
                <Typography sx={{ fontSize: '20px', fontWeight: 'bold', color: '#262D6D' }}>
                    {t('control.centerControl.auditDims.reportBadDims')}
                </Typography>
            </DialogTitle>
            <DialogContent>
                {submitted ? (
                    <Typography sx={{ py: 1 }}>
                        {errorText || (t('control.centerControl.auditDims.dimsSubmittedMessage') || 'Dimensions submitted, you may now close this window')}
                    </Typography>
                ) : (
                    <Grid container direction="column" spacing={2} sx={{ pt: 1 }}>
                        <Grid item>
                            <TextField
                                label={t('control.centerControl.auditDims.length')}
                                variant="outlined"
                                type="number"
                                value={length}
                                onChange={(e) => setLength(e.target.value)}
                                helperText={unitLabel}
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item>
                            <TextField
                                label={t('control.centerControl.auditDims.width')}
                                variant="outlined"
                                type="number"
                                value={width}
                                onChange={(e) => setWidth(e.target.value)}
                                helperText={unitLabel}
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        <Grid item>
                            <TextField
                                label={t('control.centerControl.auditDims.height')}
                                variant="outlined"
                                type="number"
                                value={height}
                                onChange={(e) => setHeight(e.target.value)}
                                helperText={unitLabel}
                                fullWidth
                                inputProps={{ min: 0 }}
                            />
                        </Grid>
                        {errorText && (
                            <Grid item>
                                <Typography color="error" variant="body2">{errorText}</Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button className="Blue-Button" onClick={onClose}>
                    {t('control.centerControl.auditDims.close')}
                </Button>
                {!submitted && (
                    <Button
                        className="Blue-Button"
                        onClick={handleSubmit}
                        disabled={loading || !length || !width || !height}
                    >
                        {loading
                            ? <CircularProgress size={16} sx={{ color: '#FFFFFF' }} />
                            : (t('control.centerControl.auditDims.submit') || 'Submit')}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
}
